import { Head, Link, router } from "@inertiajs/react"
import { Lock, Plus, Trash2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { pricingPath, signPdfPath, signaturesPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Signature {
  id: number
  label: string | null
  url: string
}

interface SignaturesIndexProps {
  signatures: Signature[]
  plan: "free" | "plus" | "pro"
}

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Signatures",
    href: signaturesPath(),
  },
]

const MAX_SIGNATURES = 5

export default function SignaturesIndex({
  signatures,
  plan,
}: SignaturesIndexProps) {
  const isPremium = plan !== "free"

  const handleDelete = (id: number) => {
    router.delete(`/signatures/${id}`, { preserveScroll: true })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Signatures" />

      <div className="flex h-full flex-1 flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4">
          {!isPremium ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Saved Signatures</CardTitle>
                  <Lock className="text-muted-foreground h-4 w-4" />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Save your signatures for quick reuse.
                </p>
                <Button className="mt-4" asChild>
                  <Link href={pricingPath()}>Upgrade to Plus</Link>
                </Button>
              </CardContent>
            </Card>
          ) : signatures.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Saved Signatures</CardTitle>
                <CardDescription>
                  Save your signature for quick access when signing PDFs.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" asChild>
                  <Link
                    href={signPdfPath()}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add signature
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Saved Signatures</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  {signatures.map((sig) => (
                    <div
                      key={sig.id}
                      className="group relative rounded-lg border p-2"
                    >
                      <img
                        src={sig.url}
                        alt={sig.label ?? "Signature"}
                        className="h-16 w-24 object-contain"
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
                        onClick={() => handleDelete(sig.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  {signatures.length < MAX_SIGNATURES && (
                    <Button variant="outline" className="h-20 w-28" asChild>
                      <Link
                        href={signPdfPath()}
                        className="flex flex-col items-center gap-1"
                      >
                        <Plus className="h-4 w-4" />
                        <span className="text-xs">Add</span>
                      </Link>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
